import type { Breadcrumb, ErrorEvent } from '@sentry/vue'
import { SENSITIVE_QUERY_KEYS } from '@/compositions/securedQueryParams'

// the same auth params the router guard strips from the URL (goat_token, goat_email, goat_app, unsubscribe token) must
// never reach Sentry either (event URL, extras, breadcrumbs) - e.g. an error thrown before the guard redirect

export const REDACTED = '[redacted]'

const MAX_DEPTH = 3

const sensitiveKeysLower = new Set<string>(SENSITIVE_QUERY_KEYS.map(key => key.toLowerCase()))

// `key=value` after a param boundary (start, ?, &, #, ;, whitespace - also URL-encoded ? and & of a nested URL),
// key matched whole and case-insensitively (my_token= / tokenX= stay untouched); the value runs to the next separator
const sensitiveParamRegExp = new RegExp(
  `(^|[?&#;\\s]|%3F|%26)(${SENSITIVE_QUERY_KEYS.join('|')})(=|%3D)[^&#\\s"'<>]+`,
  'gi'
)

export function redactSensitiveParams(text: string): string {
  return text.replace(sensitiveParamRegExp, `$1$2$3${REDACTED}`)
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object') {
    return false
  }
  const proto = Object.getPrototypeOf(value)
  return proto === Object.prototype || proto === null
}

// strings, arrays and plain objects (depth-limited); class instances (Error, Response…) are left to Sentry's normalizer.
// Object keys are not matched against the list on purpose - only string values are scanned (same as in the store).
function redactValue(value: unknown, depth = 0): unknown {
  if (typeof value === 'string') {
    return redactSensitiveParams(value)
  }
  if (depth >= MAX_DEPTH) {
    return value
  }
  if (Array.isArray(value)) {
    return value.map(item => redactValue(item, depth + 1))
  }
  if (isPlainObject(value)) {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, redactValue(item, depth + 1)]))
  }
  return value
}

type QueryString = NonNullable<NonNullable<ErrorEvent['request']>['query_string']>

function redactQueryString(query: QueryString): QueryString {
  if (typeof query === 'string') {
    return redactSensitiveParams(query)
  }
  const redactPair = (key: string, value: string) => sensitiveKeysLower.has(key.toLowerCase()) ? REDACTED : redactSensitiveParams(value)
  if (Array.isArray(query)) {
    return query.map(([key, value]): [string, string] => [key, redactPair(key, value)])
  }
  return Object.fromEntries(Object.entries(query).map(([key, value]) => [key, redactPair(key, value)]))
}

export function redactBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  if (breadcrumb.message) {
    breadcrumb.message = redactSensitiveParams(breadcrumb.message)
  }
  if (breadcrumb.data) {
    // xhr/fetch: data.url, navigation: data.from / data.to, console: data.arguments
    breadcrumb.data = redactValue(breadcrumb.data) as Breadcrumb['data']
  }
  return breadcrumb
}

// Referer (cross-origin: origin only; same-origin: tokens already stripped by the router guard) and Vue props
// (no URL with a token is passed as a prop) are not scanned - no real leak path there
export function redactSentryEvent(event: ErrorEvent): ErrorEvent {
  const request = event.request
  if (request) {
    if (request.url) {
      request.url = redactSensitiveParams(request.url)
    }
    if (request.query_string) {
      request.query_string = redactQueryString(request.query_string)
    }
  }
  if (event.message) {
    event.message = redactSensitiveParams(event.message)
  }
  event.exception?.values?.forEach((exception) => {
    if (exception.value) {
      exception.value = redactSensitiveParams(exception.value)
    }
  })
  if (event.extra) {
    event.extra = redactValue(event.extra) as ErrorEvent['extra']
  }
  event.breadcrumbs?.forEach(redactBreadcrumb)
  return event
}
